import React, { useState, useEffect } from 'react';
import { X, Sun, Moon, Check, ToggleLeft, ToggleRight, Download } from 'lucide-react';
import Dialog from './Dialog';
import ThemeService from '../services/ThemeService';

const WorkspaceSettingsDialog = ({ 
  isOpen, 
  onClose, 
  includeBlankCells,
  onToggleBlankCells,
  isDarkMode,
  setIsDarkMode,
  autoHideThumbnailBar,
  onToggleAutoHideThumbnailBar,
  includeOriginalImages,
  onToggleIncludeOriginalImages 
}) => {
  const [theme, setTheme] = useState(ThemeService.getCurrentTheme());

  // Update state when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Ensure we get the latest theme when dialog opens
      const currentTheme = ThemeService.getCurrentTheme();
      setTheme(currentTheme);
      console.log('Dialog opened with theme:', currentTheme);
    }
  }, [isOpen]);

  const handleThemeChange = (newTheme) => {
    // Apply theme change immediately - update both ThemeService and parent component state
    ThemeService.setTheme(newTheme);
    setTheme(newTheme);
    setIsDarkMode(newTheme === ThemeService.DARK); // Update parent component state directly
    console.log('Theme changed to:', newTheme);
  };

  const handleBlankCellsToggle = () => {
    onToggleBlankCells(!includeBlankCells);
  };

  const handleAutoHideToggle = () => {
    onToggleAutoHideThumbnailBar(!autoHideThumbnailBar);
  };

  const handleIncludeOriginalImagesToggle = () => {
    onToggleIncludeOriginalImages(!includeOriginalImages);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      {/* Improved proportions and added scrolling */}
      <div className="w-full max-w-6xl mx-auto dark:bg-gray-800 rounded-xl shadow-xl" style={{ maxHeight: '90vh' }}>
        {/* Fixed header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-8 py-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Workspace Settings</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X size={24} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>
        
        {/* Scrollable content */}
        <div className="overflow-y-auto px-8 py-6" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          <div className="space-y-8">
            {/* Theme setting */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Appearance</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleThemeChange(ThemeService.LIGHT)}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    theme === ThemeService.LIGHT 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                  }`}
                  type="button"
                >
                  <div className="flex flex-col items-center gap-3">
                    <Sun size={32} className={theme === ThemeService.LIGHT ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} />
                    <span className={`font-semibold text-base ${
                      theme === ThemeService.LIGHT ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                    }`}>Light Mode</span>
                  </div>
                  {theme === ThemeService.LIGHT && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check size={16} className="text-white" />
                      </div>
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => handleThemeChange(ThemeService.DARK)}
                  className={`relative p-6 rounded-xl border-2 transition-all ${
                    theme === ThemeService.DARK
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                  }`}
                  type="button"
                >
                  <div className="flex flex-col items-center gap-3">
                    <Moon size={32} className={theme === ThemeService.DARK ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} />
                    <span className={`font-semibold text-base ${
                      theme === ThemeService.DARK ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                    }`}>Dark Mode</span>
                  </div>
                  {theme === ThemeService.DARK && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check size={16} className="text-white" />
                      </div>
                    </div>
                  )}
                </button>
              </div>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                Theme preference is automatically saved and applied across all pages
              </p>
            </div>
            
            {/* Export Settings */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Export Options</h3>
              
              {/* Include Original Images Toggle */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Download size={20} className="text-gray-700 dark:text-gray-300" />
                      <h4 className="font-semibold text-gray-900 dark:text-white">Include Original Images</h4>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      Exports will include the original uploaded images or PDF pages (converted to PNG) alongside table data. 
                      Files are packaged together in a ZIP archive.
                    </p>
                  </div>
                  <button
                    onClick={handleIncludeOriginalImagesToggle}
                    className="flex-shrink-0"
                    aria-label={includeOriginalImages ? "Disable include original images" : "Enable include original images"}
                  >
                    {includeOriginalImages ? (
                      <ToggleRight size={52} className="text-blue-500" /> 
                    ) : (
                      <ToggleLeft size={52} className="text-gray-300 dark:text-gray-600" /> 
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* QuickDraw Settings */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">QuickDraw Preferences</h3>
              
              <div className="space-y-4">
                {/* Include Blank Cells Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Include Blank Cells</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Empty cells with red highlights will be included in QuickDraw review
                      </p>
                    </div>
                    <button
                      onClick={handleBlankCellsToggle}
                      className="flex-shrink-0"
                      aria-label={includeBlankCells ? "Disable include blank cells" : "Enable include blank cells"}
                    >
                      {includeBlankCells ? (
                        <ToggleRight size={52} className="text-blue-500" /> 
                      ) : (
                        <ToggleLeft size={52} className="text-gray-300 dark:text-gray-600" /> 
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Auto-hide Thumbnail Bar Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Auto-hide Thumbnail Bar</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        Thumbnail bar automatically hides when clicking outside of it
                      </p>
                    </div>
                    <button
                      onClick={handleAutoHideToggle}
                      className="flex-shrink-0"
                      aria-label={autoHideThumbnailBar ? "Disable auto-hide thumbnail bar" : "Enable auto-hide thumbnail bar"}
                    >
                      {autoHideThumbnailBar ? (
                        <ToggleRight size={52} className="text-blue-500" /> 
                      ) : (
                        <ToggleLeft size={52} className="text-gray-300 dark:text-gray-600" /> 
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Fixed footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-8 py-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-700 transition-colors font-semibold"
            type="button"
          >
            Done
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default WorkspaceSettingsDialog;